import { activatePrimaryAccount } from '#pplx/frontend/apps/ask/auth/activateMultiaccount.js';
import { setAccountForTab } from '#pplx/frontend/apps/ask/auth/setAccountForTab.js';
import { resolveBootstrapConfig } from '#pplx/frontend/apps/ask/spa-libs/resolveBootstrapConfig.js';
import { installLastActiveAccountTracking } from '#pplx/frontend/browser-storage/lastActiveAccount.js';
/**
 * Binds this tab's active account (multi-account web) with the primary-account fallback — the one
 * precedence rule every SPA bootstrap path must share:
 *
 *   1. `setAccountForTab()` binds the tab from the `?pplx_account=` deep-link / per-tab
 *      sessionStorage / last-visible seed. This must happen **before** the session is fetched, so
 *      `X-Pplx-Account` is set on that request and the cached session is read from the active
 *      account's namespace. `resolveSession` is therefore invoked *after* the bind, letting a
 *      caller that fetches the session lazily preserve that ordering.
 *   2. When nothing more specific bound the tab, fall back to activating the signed-in primary
 *      (turns on `X-Pplx-Account`, migrates its existing storage into the namespace).
 *
 * Keeping this in one place is what stops the SPA bootstrap call sites (home resolve, aux resolve,
 * prerender activation) from drifting on the sequence — the class of bug behind ENT-6254.
 */
export function bindActiveAccountForTab(resolveSession) {
    const activeAccountId = setAccountForTab();
    const resolved = resolveSession();
    if (!activeAccountId) {
        activatePrimaryAccount({
            cachedSession: resolved.cachedSession,
            sessionFetchPromise: resolved.sessionFetchPromise,
        });
    }
    return { ...resolved, activeAccountId };
}
/**
 * Resolves this tab's active account and then the bootstrap config, in the one order that keeps
 * them consistent: the tab is bound (with primary-account fallback) via {@link bindActiveAccountForTab}
 * around the session fetch in {@link resolveBootstrapConfig}.
 *
 * Every SPA entry point that renders authenticated pages must go through this. It used to be
 * inlined in the home spa bootstrap only; the aux-app bootstrap (which serves org onboarding at
 * `/onboarding/org/create`) skipped it and resolved the session against the primary/previous
 * account, leaking that account's subscription tier into a freshly switched-to account (ENT-6254).
 */
export function resolveBootstrapConfigForTab() {
    const resolved = bindActiveAccountForTab(resolveBootstrapConfig);
    installLastActiveAccountTracking();
    return resolved;
}
//# sourceMappingURL=resolveBootstrapConfigForTab.js.map